import isEmpty from "lodash/isEmpty";
import trim from "lodash/trim";
import isEqual from "lodash/isEqual";
import uniqBy from "lodash/uniqBy";

import DesignNavigationItem from "./designNavigationItem";
import {Exception} from "../common/exception";
import {
  filterFeaturesByValues,
  getAllFeatureFilters,
  getFilterGroupValue,
  getFilterGroupValues
} from "../../misc/utils";
import store from "../../store";
import featureIsMatchingDisplayLogic, {checkIfInCatProperty} from "../../misc/displayLogic";
import {addDisplayLogicCartItem, addToCart} from "../../actions/actionFunctions";
import {capitalizeFirstLetters} from "../../misc/helpers";
import {IS_LIXIL_UI, IS_QKB_UI} from "../../config";
import {LOOK_ROOM_TYPES} from "../look/core/constants";
import {getCurrentRoomType} from "../appLayout";
import {getOptionsFromNavItemsForFeature} from "../look/lookNavigation/lookFunctions";
import {getColorFilterValue} from "../../misc/filtersFunctions";
import {loadRelatedFeatures, setRoomNotInVisualizerProperty} from "./featuresFunctions";
import {getFeatureCropCoors} from "../../misc/metaDataFunctions";
import {CABINET_GROUP, COUNTERTOP_GROUP} from "../kvComponent/core/constants";

export default class PartCommon { // Common Class for KV & BV common methods (Feature -> IntermediatePart -> Part -> PartCommon)

  loadCommon(relatedItemName, RelatedItemClass, dbFeature, loadFeature = true, shouldLoadInDifferentGroups = false) {
    if (relatedItemName) {
      if (!this[relatedItemName]) {
        this[relatedItemName] = new RelatedItemClass(true);
        this.relatedItems.push(this[relatedItemName]);
      }
      if (!this.categoryEnabled) {
        this.categoryEnabled = true
      }
      this[relatedItemName].getParent = () => this;
      if (loadFeature) {
        this[relatedItemName].load(dbFeature);
      }
      return !shouldLoadInDifferentGroups
    }
    return false
  }

  createRelatedItems() {
    if (this.relatedGroupNames) {
      this.relatedGroupNames.forEach(groupName => {
        this.load({groupName}, false)
      })
    }
  }

  navigationNoMetaSubItems(filters, searchText) {
    return this.returnUniqueNavFeatures(this.getFeaturesBySearchText(this.getFilteredFeaturesByFilters(filters, this.features), searchText))
  }

  /**
   * Returns an array of navigation items related to the given parent item
   * i.e. Floor - will return all Floor features
   * @returns {Array.<DesignNavigationItem>}
   */
  navigationSubItems(filters, searchText) {
    return this.getFeaturesBySearchText(this.getFilteredFeaturesByFilters(filters), searchText)
      .map(_ => new DesignNavigationItem(_.featureName, _)); // 1) filter them by filters 2) filter them by search
  }

  navigationSubItemsWithFilterWithInactive(propName, propValue, featuresToCheck = this.features) {
    const refactoredArray = featuresToCheck.map((feature) => {
      feature.matchesMetaFailed = !(feature.matchesMetaForCondition(propName, propValue));
      return feature
    });
    return refactoredArray.map(_ => new DesignNavigationItem(_.featureName, _));
  }

  /**
   * Called before setLayers, so that each Feature can set the zIndex for its layers
   * @param zIndexes {object}
   */
  setZIndex(zIndexes) {
    if(zIndexes) {
      this.getRelatedItems().forEach(_ => _.setZIndex(zIndexes));
    }
  }

  /**
   * Each feature will load its data from teh data object which comes from REST
   * @param dbFeature {object}
   * @return boolean // needed for break logic
   */
  load(dbFeature) {
    console.log('exception load', this, dbFeature);
    Exception.NotImplementedBaseClass();
  }

  /**
   * Will return the list of sub-parts if any, i.e. for Vanity it will be Mirror, Toilet, Sink, ...
   * !@param withHidden {boolean}
   * @returns {Array.<DesignNavigationItem>}
   */
  subParts(withHidden = true) {
    return this.getRelatedItems()
      .filter(item => this.filterRelatedItem(item, withHidden))
      .map(_ => new DesignNavigationItem(_.partName, _));
  }

  filterRelatedItem(relatedItem, withHidden) {
    return withHidden
      ? relatedItem.visible && relatedItem.featureGroupEnabled
      : relatedItem.visible
      && relatedItem.featureGroupEnabled
      && (relatedItem.featureGroupVisible || (!relatedItem.featureGroupVisible && document.kvConfig.isBackend))
  }

  /**
   * Returns the navigation item to be displayed in navigation bar
   */
  navigationItem() {
    Exception.NotImplementedBaseClass(this);
  }

  /**
   * In case of Vanity is Custom & Complete.
   * Usually called for Complex parts, such as Vanities
   */
  subTypes() {
    // Exception.NotImplementedBaseClass(this);
    return []
  }

  /**
   * Converts a given sub-type to string
   // * @param subType
   * @returns {null}
   */
  subTypeToString() {
    return null;
  }

  /**
   * Checked whether the given sub-type has items, i.e. Vanity.Complete has
   */
  isSubTypeWithItems() {
    Exception.NotImplementedBaseClass(this);
  }

  activeSubType() {
    Exception.NotImplementedBaseClass(this);
  }

  getFilteredFeaturesByFilters(filters, featuresArray = this.features) {
    return !isEmpty(filters) ? filterFeaturesByValues(featuresArray, filters) : (featuresArray || []);
  }

  getFeaturesBySearchText(arrayToFilter, searchText) {
    if (!searchText) {
      return arrayToFilter
    }
    if (IS_LIXIL_UI) {
      return this.getFeaturesBySearchTextLixil(arrayToFilter, searchText)
    }
    const filterGroups = store.getState().filterState.filterGroups;
    const _searchText = trim(searchText.toLowerCase());
    return arrayToFilter.filter(feature => {
      let res = (!!feature);
      if (res) {
        const featureFilterValueIds = Object.assign(feature.dbFeature.filterValueIDs, feature.dbFeature.fabFilterValueIDs);
        let isPartName = feature.getFeatureName && feature.getFeatureName().toLowerCase().includes(_searchText);
        let isFilterName = false;

        if(!isPartName) {
          for(let filterGroupId in featureFilterValueIds) {
            let filterGroupIds = featureFilterValueIds[filterGroupId];
            for(let filterIndex in filterGroupIds) {
              let filterId = filterGroupIds[filterIndex];
              if(filterGroups[filterGroupId] && filterGroups[filterGroupId].values) {
                let filterGroup = filterGroups.find(fg => fg.id === filterGroupId);
                if (filterGroup && filterGroup.values) {
                  let filter = filterGroup.values.find( fgv => fgv.id === filterId);
                  if (filter) {
                    isFilterName = (isFilterName || filter.displayText.toLowerCase().includes(_searchText) || filter.value.toLowerCase().includes(_searchText));
                  }
                }
              }
            }
          }
        }

        res = isPartName || isFilterName;
      }
      return res;
    })
  }

  getFeaturesBySearchTextLixil(arrayToFilter, searchText) {
    const filterGroups = store.getState().filterState.filterGroups;
    const collectionFilterGroup = filterGroups.find(group => group.name === "Collection");
    const _searchText = searchText.trim().toLowerCase();

    return arrayToFilter.filter(feature => {
      const featureFilterValueIds = getAllFeatureFilters(feature);
      const sku = (feature.dbFeature.sku || "").toLowerCase();
      const customDisplayName = feature.getFeatureName().toLowerCase();

      let res = (!!feature);
      if (res) {
        let matchByName = (sku && sku.includes(_searchText)) || (customDisplayName && customDisplayName.includes(_searchText));
        let isFilterName = false;

        if (!matchByName && collectionFilterGroup && Array.isArray(featureFilterValueIds[collectionFilterGroup.id])) {
          matchByName = featureFilterValueIds[collectionFilterGroup.id].some(filterId => {
            return collectionFilterGroup.values.some(fValue => {
              return (fValue.id === filterId) && fValue.displayText && fValue.displayText.toLowerCase().includes(_searchText)
            })
          })
        }

        if (!matchByName) {
          for(let filterGroupId in featureFilterValueIds) {
            let filterGroupIds = featureFilterValueIds[filterGroupId];
            for(let filterIndex in filterGroupIds) {
              let filterId = filterGroupIds[filterIndex];
              if(filterGroups[filterGroupId] && filterGroups[filterGroupId].values) {
                let filterGroup = filterGroups.find(fg => fg.id === filterGroupId);
                if (filterGroup && filterGroup.values) {
                  let filter = filterGroup.values.find(fgv => fgv.id === filterId);
                  if (filter) {
                    isFilterName = (isFilterName || filter.displayText.toLowerCase().includes(_searchText) || filter.value.toLowerCase().includes(_searchText));
                  }
                }
              }
            }
          }
        }

        res = matchByName || isFilterName;
      }
      return res;
    })
  }

  /**
   * Returns the fabricator feature id
   * @returns {int}
   */
  id() {
    return this.dbFeature.id;
  }

  /**
   * Returns the fabricator feature id
   * @param featureId {number}
   * @param partsToo {boolean}
   * @returns {feature}
   */
  getFeatureById(featureId, partsToo = false) {
    const neededId = +featureId;
    if (!this.features && (!partsToo || !this.featuresParts)) {
      console.error("This featureGroup has no features array:", this)
      return null
    }
    // if (partsToo) {
    //   return this.features.find(f => f.dbFeature.id === neededId) || this.featuresParts.find(f => f.dbFeature.id === neededId)
    // }
    // return this.features.find(f => f.dbFeature.id === neededId)
    let allFeatures = [];
    if (this.partId === "fridge" && this.featuresList) {
      Object.keys(this.featuresList).forEach(layoutName => {
        allFeatures = [...allFeatures, ...this.featuresList[layoutName]]
      })
    } else {
      allFeatures = partsToo ? [...this.features, ...this.featuresParts] : this.features
    }
    for (let i = 0; i < allFeatures.length; i++) {
      if (allFeatures[i].dbFeature.id === neededId) {
        return allFeatures[i];
      }
    }
    return null
  }

  getFeatureByPartName(partName, inParts = false) {
    if (!partName) {
      return null
    }
    partName = partName.split(" ").join("").toLowerCase();  // for example, "Natural Stone Sofitel Gold" & "Natural Stone  Sofitel Gold" - different names
    const arrayToFind = inParts && this.featureParts ? this.featureParts.map(feature => { return {part: feature} }) : this.navigationSubItems();
    const matchingItem = arrayToFind.find(navFeature => {
      const feature = navFeature.part;
      if (!feature.partName || feature.notAvailableInVisualizer) {
        return false
      }
      if (!feature._partNameWithoutSpaces) {
        feature._partNameWithoutSpaces = feature.getMatchingFeatureName().split(" ").join("").toLowerCase();
      }
      return partName === feature._partNameWithoutSpaces
    });
    return matchingItem ? matchingItem.part : null
  }

  getDefaultFeature() {

  }

  /**
   * Returns the active feature id
   * @param placeholdersNeeded
   * @returns {int}
   */
  activeFeatureId(placeholdersNeeded = false) {
    return placeholdersNeeded && this.placeholderFeature && !this.placeholderChanged
      ? this.placeholderFeature.dbFeature.id
      : this.activeFeature.dbFeature.id
  }

  /**
   * @param feature {Part}
   * @returns {*}
   */
  featureDefinitionId(feature = null) {
    const dbFeature = feature ? feature.dbFeature : this.dbFeature;
    return dbFeature.featureDefinitionID;
  }

  handleClick() {}

  disabledClick() {}

  /**
   * Used for metaData conditions
   * @returns Array
   */
  getArrayOfConditions() {
    return []
  }

  updateLocalStorage() {
    const layout = this.layout || this.getParent().layout || this.getParent().getParent().layout;
    layout.updateLocalStorage();
  }

  /**
   * Used for complex parts, i.e. Vanity - Complex, Custom
   // * @param subType
   */
  handleSubTypeClick() {
    Exception.NotImplementedBaseClass();
  }

  /**
   * Handles the click on sub-part buttons, i.e. for Vanity on its buttons such as Toilet, Sink...
   // * @param subPart
   */
  handleSubPartClick() {
    // Exception.NotImplementedBaseClass();
  }

  /**
   * Returns first value Id from a specific group
   * @param groupId
   */
  filterValue(groupId) {
    return this.dbFeature.filterValueIDs[groupId][0];
  }

  /**
   * Do something specific after layer changes
   // * @param layoutType
   */
  changeLayout() {

  }

  /**
   * Do something specific after Type Changed (returns null by default for all Parts)
   */
  setDefaultPartAfterSubTypeClick() {
    return null
  }

  getRelatedItems() {
    return this.relatedItems
  }

  /**
   * Set default layer for each relatedItem of the group.
   * @param layout {Layout}
   */
  setDefault(layout) {
    this.relatedItems.forEach(relatedItem => relatedItem.setDefault(layout));
  }

  setFeatureGroupDefault(layout, setNotInVisFeature = this.hasNoVisualizerLayers) {
    const conditions = this.getArrayOfConditions ? this.getArrayOfConditions() : null;
    let filteredFeatures = conditions ? this.features.filter(feature => {
      return setNotInVisFeature
        ? feature.matchesMetaForArrayOfConditions(conditions)
        : feature.dbFeature.s3Bindings && feature.matchesMetaForArrayOfConditions(conditions)
    }) : this.features;

    const isBV6 = document.kvConfig.visualizerType === "bvLook"; // for new bathroom only for now! to test
    if (isBV6 && this.activeFeature && this.selectedFeaturesHistory && !this.selectedFeaturesHistory.includes(this.activeFeature.getMatchingFeatureName())) {
      this.selectedFeaturesHistory.push(this.activeFeature.getMatchingFeatureName())
    }

    if (isBV6 && !filteredFeatures.length
      && (getCurrentRoomType(layout) === LOOK_ROOM_TYPES.as) && !(this.partId.includes("_wall") && this.hasAdditionalParts)) {
      filteredFeatures = this.getArrayOfConditions
        ? this.featuresParts.filter(f => f.dbFeature.s3Bindings && f.matchesMetaForArrayOfConditions(conditions))
        : this.featuresParts;
    }

    const matchingFeatureExist = this.tryToSetTheSameFeature(filteredFeatures, null);
    if (!matchingFeatureExist) {
      this.activeFeature = filteredFeatures[0];
    }
    const features = this.features;
    if (IS_LIXIL_UI && !this.activeFeature && !this.activeNotInVisualizerFeature && Array.isArray(features)
      && features.length && features.every(feature => feature.notAvailableInVisualizer)) {
      this.activeNotInVisualizerFeature = features[0];
    }
    // this.placeholderFeature = this.features.find((feature) => {
    //   return feature.dbFeature.displayName && feature.dbFeature.displayName.includes("laceholder");
    // });
    if (this.hasAdditionalParts) {
      this.activeFeatureParts = this.setActiveFeatureParts();
    }
    if (this.layerPositions) {
      if (this.activeFeature) {
        this.setLayerPositions()
      } else {
        Object.keys(this.layerPositions).forEach(key => {
          this.layerPositions[key] = null;
        })
      }
    }
  }

  tryToSetTheSameFeature(filteredFeatures = [], partName = "") {
    const currentActiveFeature = this.activeFeature;
    if (currentActiveFeature && currentActiveFeature.partName) {
      let splittedFromTheLayoutName = partName && this.removeLayoutFromPartName
        ? this.removeLayoutFromPartName(partName)
        : this.activeFeature.getMatchingFeatureName();
      this.activeFeature = filteredFeatures.find(feature => {
        return feature.getMatchingFeatureName() === splittedFromTheLayoutName
      });
      if (!this.activeFeature) {
        const filterGroups = store.getState().filterState.filterGroups;
        const colorFilterValue = getColorFilterValue(currentActiveFeature, filterGroups);
        if (colorFilterValue) {
          this.activeFeature = filteredFeatures.find(feature => {
            const currentColorFilterValue = getColorFilterValue(feature, filterGroups);
            return currentColorFilterValue && currentColorFilterValue.id === colorFilterValue.id
          });
        }
      }
    }
    if (!this.activeFeature && this.selectedFeaturesHistory) {
      for (let featureName of this.selectedFeaturesHistory.slice(0, 5)) { // check first five, no need to go further
        const foundFeature = this.getFeatureByPartName(featureName);
        if (foundFeature) {
          const prevFeatureMatchingName = foundFeature.getMatchingFeatureName();
          const foundFinally = filteredFeatures.find(feature => {
            return feature.getMatchingFeatureName() === prevFeatureMatchingName
          });
          if (foundFinally) {
            this.activeFeature = foundFinally;
            break;
          }
        }
      }
    }
    return !!this.activeFeature
  }

  getFeatureName = (forQtCategory = "") => {
    if (!this._featureName) {
      const originalName = this.metaData.ActualDisplayedName
        ? this.dbFeature.customDisplayName && (this.dbFeature.customDisplayName !== this.dbFeature.sharedFeatureName)
          ? this.dbFeature.customDisplayName
          : this.metaData.ActualDisplayedName[0]
        : this.dbFeature.customDisplayName || this.partName || "";
      const parent = this.getParent();
      this._featureName = (originalName || "").replace((this.dbFeature.customGroupName || parent.dbName || parent.partName) + " - ", "");
    }
    if (forQtCategory) {
      return checkIfInCatProperty(this, "inCatName", this._featureName, forQtCategory) || this._featureName || "";
    }
    return this._featureName
  };

  getLowerCasedCustomDisplayName = () => {
    if (!this._lowercaseCustomDisplayName) {
      this._lowercaseCustomDisplayName = (this.dbFeature.customDisplayName || this.partName || '')
        .trim()
        .toLowerCase()
        .replace('room - ', '')
    }
    return this._lowercaseCustomDisplayName;
  };

  getLowerCasedSharedFeatureName = () => {
    if (!this._lowercaseSharedFeatureName) {
      this._lowercaseSharedFeatureName = (this.dbFeature.sharedFeatureName || this.partName || '')
        .trim()
        .toLowerCase()
        .replace('room - ', '')
    }
    return this._lowercaseSharedFeatureName;
  };

  /**
   * @param dbFeaturesArray {[dbFeatureInterface]}
   * @param pageSize {number}
   * @param setNextPage {boolean}
   * @param layoutAbbr {string}
   * @param loadedPage {number|null}
   * @param fromSearch {boolean}
   * @param setCustomConfigurationsAfter {boolean}
   */
  setGroupFeatures(dbFeaturesArray,
                   pageSize = 1000,
                   setNextPage = true,
                   layoutAbbr = "",
                   loadedPage = null,
                   fromSearch = false,
                   setCustomConfigurationsAfter = true) {

    const layout = this.layout || this.getParent().layout;
    const layoutToSet = layoutAbbr || layout.getCurrentLayoutAbbreviation();

    if (setNextPage) {
      // this.nextFeaturesPageToLoad += 1;
      let nextFeaturesPageToLoad = 1;
      const addFromPageSize = pageSize === 6000 ? 3 : 1;
      if (this.nextFeaturesPageToLoadArray) {
        nextFeaturesPageToLoad = Number.isInteger(loadedPage)
          ? this.nextFeaturesPageToLoadArray[layoutToSet].page + addFromPageSize
          : this.nextFeaturesPageToLoadArray[layoutToSet].page + 1
      } else {
        nextFeaturesPageToLoad = Number.isInteger(loadedPage)
          ? this.nextFeaturesPageToLoad + addFromPageSize
          : this.nextFeaturesPageToLoad + 1;
      }
      // let nextFeaturesPageToLoad = Number.isInteger(loadedPage)
      //   ? this.nextFeaturesPageToLoad + (pageSize === 6000 ? 3 : 1)
      //   : this.nextFeaturesPageToLoadArray ? this.nextFeaturesPageToLoadArray[layoutToSet].page + 1 : this.nextFeaturesPageToLoad + 1;
      // if ((pageSize / 1000 - 1) === 1) {
      //   nextFeaturesPageToLoad += 1
      // }
      this.nextFeaturesPageToLoad = nextFeaturesPageToLoad;
      if (this.nextFeaturesPageToLoadArray) {
        this.nextFeaturesPageToLoadArray[layoutToSet].page = nextFeaturesPageToLoad;
      }
      this.allFeaturesReceived = true;
    }
    if (!fromSearch) {
      if (this.nextFeaturesPageToLoadArray) {
        this.nextFeaturesPageToLoadArray[layoutToSet].allReceived = dbFeaturesArray.length < pageSize;
        const {bvPresets} = store.getState().bathroomVisualizerState;
        if (bvPresets && bvPresets.length) {
          const uniqueLayoutsTypes = bvPresets.reduce((total, preset) => {
            if (!total.some(layoutType => layoutType === preset.layout_type)) {
              total.push(preset.layout_type)
            }
            return total
          }, []);
          this.realFeaturesReceived = uniqueLayoutsTypes.every(layoutNumber => {
            const layoutName = layout.getCurrentLayoutName(layoutNumber);
            return this.nextFeaturesPageToLoadArray[layoutName] && this.nextFeaturesPageToLoadArray[layoutName].allReceived
          });
        } else {
          this.realFeaturesReceived = dbFeaturesArray.length < pageSize;
        }
      } else {
        this.realFeaturesReceived = dbFeaturesArray.length < pageSize;
      }
    }

    layout.loadNewFeatures(dbFeaturesArray, this, setCustomConfigurationsAfter);

  }

  getRealFeaturesReceivedProperty = () => {
    const layout = this.layout || this.getParent().layout;
    const currentLayoutAbbr = layout.getCurrentLayoutAbbreviation();
    return this.nextFeaturesPageToLoadArray
      ? this.nextFeaturesPageToLoadArray[currentLayoutAbbr].allReceived
      : this.realFeaturesReceived
  }

  getNextPageToLoad = (currentLayoutAbbr) => {
    // intermediatePart
    if (this.nextFeaturesPageToLoadArray) {
      if (!currentLayoutAbbr) {
        const layout = this.layout || this.getParent().layout;
        currentLayoutAbbr = layout.getCurrentLayoutAbbreviation();
      }
      return this.nextFeaturesPageToLoadArray[currentLayoutAbbr].page
    }
    return this.nextFeaturesPageToLoad
  }

  getCollectionName = (filterGroups) => {
    if (this._collectionName) {
      return this._collectionName
    }
    const collectionFg = filterGroups ? filterGroups.find(fGroup => fGroup.name === "Collection") : null;
    if (collectionFg) {
      const featureCollectionValue = getFilterGroupValues(this, collectionFg);
      if (Array.isArray(featureCollectionValue)) {
        const featureCollectionValueObject = getFilterGroupValue(featureCollectionValue[0], collectionFg);
        if (featureCollectionValueObject) {
          this._collectionName = capitalizeFirstLetters(featureCollectionValueObject.displayText);
          return this._collectionName
        }
      }
    }
    return ""
  }

  getFeatureSavedFilterName = (filterGroups, filterName, property) => {
    if (!this._filterNames) {
      this._filterNames = {};
    }
    if (this._filterNames[property]) {
      return this._filterNames[property]
    }
    const collectionFg = filterGroups ? filterGroups.find(fGroup => fGroup.name === filterName) : null;
    if (collectionFg) {
      const featureCollectionValue = getFilterGroupValues(this, collectionFg);
      if (Array.isArray(featureCollectionValue)) {
        const featureCollectionValueObject = getFilterGroupValue(featureCollectionValue[0], collectionFg);
        if (featureCollectionValueObject) {
          this._filterNames[property] = capitalizeFirstLetters(featureCollectionValueObject.displayText || featureCollectionValueObject.value);
          return this._filterNames[property]
        }
      }
    }
    return ""
  }

  getColorName = (filterGroups) => {
    if (this._colorName) {
      return this._colorName
    }
    const colorFilterGroup = filterGroups ? filterGroups.find(fGroup => fGroup.name === "Color") : null;

    if (colorFilterGroup) {
      const featureCollectionValue = getFilterGroupValues(this, colorFilterGroup);
      if (Array.isArray(featureCollectionValue)) {
        const featureCollectionValueObject = getFilterGroupValue(featureCollectionValue[0], colorFilterGroup);
        if (featureCollectionValueObject) {
          this._colorName = capitalizeFirstLetters(featureCollectionValueObject.displayText);
          return this._colorName
        }
      }
    }
    return ""
  }

  getCustomFullDisplayName = () => {
    return this.getSplittedFeatureName() // is different in bvLook visualizer
  }

  getMatchingOptionsName = () => {
    return this.getSplittedFeatureName() // is different in bvLook visualizer
  }

  /**
   * Update visibility for all layers.
   * Each feature decides on its own what layers are needed.
   * In case when feature must be invisible it must return null, not an empty array
   * @param layout {Layout}
   * @param placeholdersNeeded {boolean}
   */
  setLayers(layout, placeholdersNeeded) {
    let layers = [];
    this.getRelatedItems()
      .filter(relItem => relItem.featureGroupEnabled)
      .forEach(relatedItem => {
        relatedItem.setLayers(layout);
        let activeFeatures = [];
        if (relatedItem.layerPositions) {
          Object.keys(relatedItem.layerPositions).forEach(key => {
            if (relatedItem.layerPositions[key]) {
              activeFeatures.push(relatedItem.layerPositions[key])
            }
          })
        } else {
          if (placeholdersNeeded && !relatedItem.placeholderChanged && relatedItem.placeholderFeature) {
            activeFeatures.push(relatedItem.placeholderFeature)
          } else if (relatedItem.activeFeature && relatedItem.partId !== "attachment") {
            activeFeatures.push(relatedItem.activeFeature)
          }
        }
        if (relatedItem.activeFeatureParts && relatedItem.activeFeatureParts.length) {
          activeFeatures = [...activeFeatures, ...relatedItem.activeFeatureParts]
        }
        activeFeatures.forEach((activeFeaturePart) => {
          if (activeFeaturePart && activeFeaturePart.layer.src) {
            // cabinetActiveFeature.layer.partId = cabinetActiveFeature.partId;
            activeFeaturePart.layer.coords = activeFeaturePart.metaData["Coords"];
            activeFeaturePart.layer.partId = activeFeaturePart.partId;
            activeFeaturePart.layer.layerPosition = activeFeaturePart.metaData.layerPosition;
            activeFeaturePart.layer.cropCoords = getFeatureCropCoors(activeFeaturePart);
            layers.push(activeFeaturePart.layer);
          }
        });
      });
    return layers.length ? layers : null;
  }

  // setPrevioslySelected(featureGroup) { // featureGroup is current active featureGroup from the click
  setPrevioslySelected() { // parent scope
    this.relatedItems.forEach(relatedItem => {
      relatedItem.activeFeatureBeforeAllWalls = !relatedItem.placeholderChanged
        ? "placeholder"
        : relatedItem.activeFeature;
    });
  }

  getFeaturesFromLayout = (layoutName, forSpecificScene, allFeatures) => {
    let currentLayoutFeatures = this.layerPositions
      ? [...(this.featuresList[layoutName] || []), ...(this.featuresPartsList[layoutName] || [])]
      : (this.featuresList[layoutName] || []);
    for (let i = 0; i < currentLayoutFeatures.length; i++) {
      const feature = currentLayoutFeatures[i];
      if (forSpecificScene && feature.metaData["RoomScene"] && !feature.metaData["RoomScene"].includes(forSpecificScene)) {
        continue
      }
      const featureName = feature.getSplittedFeatureName();
      if (!allFeatures.some(addedFeature => addedFeature.getSplittedFeatureName() === featureName)) {
        allFeatures.push(feature);
      }
    }
    return allFeatures
  }

  getFeaturesForNavigationFrom = (restOptions = {}) => {
    const {returnAllFeatures, forSpecificLayout, forSpecificScene} = restOptions;
    if (returnAllFeatures) {
      const layout = this.getParent().layout;
      let allFeatures = [];
      // 1) first add current layout features or specific layout features
      allFeatures = this.getFeaturesFromLayout(forSpecificLayout || ("Layout" + layout.type), forSpecificScene, allFeatures)
      if (forSpecificLayout) {
        return allFeatures
      }
      // 2) if we need all features - check another layouts
      const {bvPresets} = store.getState().bathroomVisualizerState;
      // const checkLayouts = layoutNames.filter(layoutName => {
      //   if (forSpecificLayout) {
      //     return forSpecificLayout === layoutName
      //   }
      //   return bvPresets.some(bvPreset => bvPreset.layout_type === +(layoutName.replace("Layout", "")))
      // })

      // 2) next popular rooms are 11 for AS and 3 for grohe, check them
      if (layout.type !== 11 && (document.kvConfig.visualizerType === "bvLook")) {
        allFeatures = this.getFeaturesFromLayout("Layout11", "1", allFeatures);
      }
      // if (layout.type !== 3) {
      //   allFeatures = this.getFeaturesFromLayout("Layout3", "1", allFeatures);
      // }
      // 3) select all features from scene 1
      bvPresets.forEach(bvPreset => {
        if (bvPreset.layout_type === layout.type) {
          return;
        }
        const stringifiedLayout = "Layout" + bvPreset.layout_type;
        allFeatures = this.getFeaturesFromLayout(stringifiedLayout, "1", allFeatures);
      });
      // 4) check skipped layouts to get all other features another room scenes features
      const layoutNames = Object.keys(layout.layoutNames).filter(key => key !== "All");
      layoutNames.forEach((layoutType) => {
        allFeatures = this.getFeaturesFromLayout(layoutType, null, allFeatures);
      });
      // console.log("allFeatures", allFeatures);
      return allFeatures
    }
    return this.layerPositions ? [...this.features, ...this.featuresParts] : this.features
  }

  checkAutoAddToCart = (feature) => {
    //intermediatePart
    if (!feature.dbFeature.autoAddToCart) {
      return;
    }
    const parentGroup = this.getParent ? this.getParent() : null;// parent, for ex. "Appliance"
    if (parentGroup && parentGroup.layout && parentGroup.layout.isFakeLayout) {
      return;
    }
    const cartItem = {
      selectionGroup: (parentGroup && parentGroup.menuType) ? parentGroup.partName : this.partName,
      quantity: 1,
      partId: this.partId,
      cartFeature: feature
    };
    if (feature.dbFeature.displayLogic) {
      const storeState = store.getState();
      const {quantity, isMatching} = featureIsMatchingDisplayLogic(
        feature,
        storeState.cartState.cartItems,
        storeState.quotesState.activeQuoteIndex,
        storeState.wizardState.allWizardsValues,
        storeState.cartState.displayLogicCartItems,
        storeState.fabricatorState,
      );
      if (isMatching && (!storeState.cartState.cartItems.some(cItem => {
        return cItem.cartFeature && cItem.cartFeature.dbFeature && (cItem.cartFeature.dbFeature.id === feature.dbFeature.id)
      }))) {
        cartItem.quantity = quantity;
        store.dispatch(addToCart([cartItem]));
      }
      store.dispatch(addDisplayLogicCartItem(cartItem, this.getParent().layout.visualizerType));
    } else {
      store.dispatch(addToCart([cartItem]));
    }
  }

  loadFeatureCommon = (feature) => { // other logic
    this.checkAutoAddToCart(feature);
    if (feature.dbFeature.groupName === "Room") {
      feature = setRoomNotInVisualizerProperty(feature);
    }
    if (IS_LIXIL_UI && !feature.dbFeature.clickable) {
      const layout = this.getParent().layout;
      if (layout && layout.disabledClickablePartIds && !layout.disabledClickablePartIds.includes(this.partId)) {
        layout.disabledClickablePartIds.push(this.partId);
      }
    }
    // const layout = this.getParent().layout;
    // if (feature.dbFeature.isRelatedDbFeature) {
    //   if (!Array.isArray(layout.allRelatedDbFeatures)) {
    //     layout.allRelatedDbFeatures = [];
    //   }
    //   if (!layout.allRelatedDbFeatures.some(addedDbFeature => addedDbFeature.id === feature.dbFeature.id)) {
    //     layout.allRelatedDbFeatures.push(feature.dbFeature);
    //   }
    // }
    // loadRelatedFeatures(feature.dbFeature, layout);
  };

  _setLayers(layout) {
    //intermediatePart
    if (!this.disabled) {
      if (!this.activeFeature) {
        this.setDefault(layout);
      }
      if (this.layerPositions) {
        Object.keys(this.layerPositions).forEach(key => {
          if (this.layerPositions[key]) {
            if (this.layerPositions[key].matchesMetaForArrayOfConditions(this.getArrayOfConditions())) {
              this.setFeatureLayer(this.layerPositions[key], layout);
            } else {
              this.layerPositions[key].layer.src = '';
            }
          }
        })
      } else if (this.activeFeature && !this.activeFeature.notAvailableInVisualizer) {
        if (this.partId !== "attachment") { // remove activeFeature layer to prevent duplicate, it has parts
          this.setFeatureLayer(this.activeFeature, layout);
        }
        if (this.hasAdditionalParts && (document.kvConfig.visualizerType === "kvScene")) {
          this.activeFeatureParts = this.setActiveFeatureParts(); // we have to set them each time because we need to react on disabled clicks (bottom cabinet - appliances)
        }
        if (Array.isArray(this.activeFeatureParts) && this.activeFeatureParts.length) {
          this.activeFeatureParts.forEach((activeFeaturePart) => {
            this.setFeatureLayer(activeFeaturePart, layout);
          })
        }
      }
      if (this.placeholderFeature) {
        this.setFeatureLayer(this.placeholderFeature, layout);
      }
    } else {
      this.hideLayers();
    }
  }

  setFeatureLayer(currentFeature, layout) {
    //intermediatePart
    // console.log('currentFeature', currentFeature);
    const layoutAbr = layout.getCurrentLayoutAbbreviation();
    if (!currentFeature || !currentFeature.dbFeature || !currentFeature.dbFeature.s3Bindings
      || (!(currentFeature.dbFeature.metaData["RoomLayout"]) || !currentFeature.dbFeature.metaData["RoomLayout"].includes(layoutAbr))) {
      if (currentFeature) {
        currentFeature.layer.src = "";
      }
      return;
    }
    const s3Binding = currentFeature.dbFeature.s3Bindings.find(binding => binding.visualizer === layout.layersBinding);
    currentFeature.layer.src = s3Binding ? s3Binding.path : null;
    currentFeature.layer.bucketName = s3Binding ? s3Binding.bucketName : null;
  }

  featureClicked() {
    const featuresHistory = this.activeFeature ? this.selectedFeaturesHistory : this.getParent().selectedFeaturesHistory;
    const partName = this.activeFeature
      ? this.activeFeature.getMatchingFeatureName()
      : this.getMatchingFeatureName ? this.getMatchingFeatureName() : this.partName;
    if (Array.isArray(featuresHistory) && (featuresHistory[0] !== partName)) {
      featuresHistory.unshift(partName); // add in the beggining
      if (featuresHistory.length > 10) {
        featuresHistory.pop() // remove last element, no need to keep huge arrays
      }
    }
    this.updateLocalStorage();
    // other specific events, for ex. Google Analytics, etc.
  }


  // navigationSubItems(filters, searchText, forFeature, filterGroups, restOptions) { // to do
  navigationItemsWithOptions(filters, forFeature, filterGroups, featuresToDisplay) {
    // intermediate part
    let newFeatures = [];
    let navItems = [];
    const matchingFeatureProperty = this.useMatchingFeatureReplacement || "MatchingFeature";

    if (filterGroups && filterGroups.length && (!filters || !Object.keys(filters).length)
      && !IS_QKB_UI && !(CABINET_GROUP.includes(this.partId) || COUNTERTOP_GROUP.includes(this.partId))) {

      // console.log("filteredAvailableFeatures", filteredAvailableFeatures);
      // console.log("featuresToDisplay", featuresToDisplay);
      for (let i = 0; i < featuresToDisplay.length; i++) { // 1) take all others
        const currentFeature = featuresToDisplay[i];
        const colorName = currentFeature.getColorName(filterGroups);
        const matchingOptionsName = currentFeature.getMatchingOptionsName();
        let conditionPassed = !colorName
          || !newFeatures.some(feature => feature.getMatchingOptionsName() === matchingOptionsName)
          || (currentFeature.metaData[matchingFeatureProperty] && !newFeatures.some(feature => feature.metaData[matchingFeatureProperty]
                && isEqual(feature.metaData[matchingFeatureProperty], currentFeature.metaData[matchingFeatureProperty])))
        if (conditionPassed) {
          newFeatures.push(currentFeature);
        }
      }
      navItems = this.returnUniqueNavFeatures(this.getNavItemsWithOptions(forFeature, newFeatures, featuresToDisplay, filterGroups).map(featureObject => {
        return new DesignNavigationItem(featureObject.mainFeature.featureName, featureObject.mainFeature, featureObject.otherOptions)
      }), false);
    }
    if (!navItems.length) {
      navItems = this.returnUniqueNavFeatures(featuresToDisplay)
    }
    // if (this.filterNavigationItems && forFeature) {
    //   navItems = this.filterNavigationItems(navItems, forFeature)
    // }
    return navItems
  }

  /**
   * this is almost the same as feature options, it is replacing next nav items with the needed color feature
   * @param forFeature {featureInterface} featureInterface
   * @param newFeatures {[featureInterface]}
   * @param allFeatures {[featureInterface]}
   * @param filterGroups {[filterGroupInterface]}
   * @param useSpecificVanityLogic {boolean}
   * @returns {array}
   */
  getNavItemsWithOptions = (forFeature= null,
                            newFeatures = [],
                            allFeatures = [],
                            filterGroups,
                            // eslint-disable-next-line no-unused-vars
                            useSpecificVanityLogic = false) => {

    const matchingFeatureProperty = this.useMatchingFeatureReplacement || "MatchingFeature";
    const neededColorName = forFeature ? forFeature.getColorName(filterGroups) : "";

    return newFeatures.map(currentFeature => {
      const filterValue = getColorFilterValue(currentFeature, filterGroups);
      let otherOptions = getOptionsFromNavItemsForFeature(currentFeature, allFeatures, filterGroups);

      const currentFeatureColor = currentFeature.getColorName(filterGroups);
      const customFullDisplayName = currentFeature.getMatchingOptionsName();

      if (forFeature && (currentFeature.dbFeature.id === forFeature.dbFeature.id || (
        (customFullDisplayName === forFeature.getMatchingOptionsName())
        && (!forFeature.metaData[matchingFeatureProperty] || isEqual(forFeature.metaData[matchingFeatureProperty], currentFeature.metaData[matchingFeatureProperty]))
        && (currentFeatureColor && forFeature.getColorName(filterGroups) && (currentFeatureColor !== forFeature.getColorName(filterGroups)))
      ))) {
        if (filterValue && !otherOptions.some(option => option.colorValue.id === filterValue.id)) {
          otherOptions.push({feature: currentFeature, colorValue: filterValue})
        }
        return {
          mainFeature: forFeature,
          otherOptions: otherOptions.filter(option => option.feature.dbFeature.id !== forFeature.dbFeature.id)
        };
      } else {
        if (!filterValue) {
          return {mainFeature: currentFeature, otherOptions: []}
        }
        const allOptions = [{feature: currentFeature, colorValue: filterValue}, ...otherOptions];
        const mainOption = (forFeature && neededColorName && allOptions.find(option => option.feature.getColorName(filterGroups) === neededColorName))
        // console.log("mainOption", mainOption);
        if (mainOption) {
          return {
            mainFeature: mainOption.feature,
            otherOptions: allOptions.filter(option => option.feature.dbFeature.id !== mainOption.feature.dbFeature.id)
          }
        }
        return {mainFeature: currentFeature, otherOptions}
      }
    });
  }

  returnUniqueNavFeatures(featuresToDisplay, createNavItems = true) {
    const navItems = createNavItems ? this.returnNavItemsFromFeatures(featuresToDisplay) : featuresToDisplay;
    if (document.kvConfig.visualizerType === "bvScene" && (this.partId ==="sink" || this.partId ==="center_island_sink"
      || this.partId ==="vanity_sink" || this.partId ==="vanity_faucet")) {
      return uniqBy(navItems, (navItem) => navItem.part.dbFeature.id && navItem.part.getSplittedFeatureName());
    }
    if (document.kvConfig.toolVersion >= 5) { // not sure if we can do that for old visualizers
      return uniqBy(navItems, (navItem) => navItem.part.dbFeature.id && navItem.part.dbFeature.sharedFeatureName);
    }
    return uniqBy(navItems, (navItem) => navItem.part.dbFeature.id);
  }

  returnNavItemsFromFeatures(featuresToDisplay) {
    return featuresToDisplay.map(_ => new DesignNavigationItem(_.featureName, _));
  }

}

PartCommon.prototype.getFeatureCurrency = function (propsFilterGroups) {
  if (Object.hasOwnProperty(this._useCurrency)) {
    return this._useCurrency
  }
  this._useCurrency = null;
  const filterGroups = propsFilterGroups || store.getState().filterState.filterGroups;
  const filterGroup = filterGroups.find(group => group.name === "Currency");
  if (!filterGroup) {
    return this._useCurrency
  }
  const featureCollectionValue = getFilterGroupValues(this, filterGroup);
  if (Array.isArray(featureCollectionValue)) {
    const featureCollectionValueObject = getFilterGroupValue(featureCollectionValue[0], filterGroup);
    if (featureCollectionValueObject) {
      this._useCurrency = featureCollectionValueObject.value
    }
  }
  return this._useCurrency
}

PartCommon.prototype.getFeatureRelatedFeatures = function () {
  if (!this._relatedFeaturesSet) {
    // console.log("loadRelatedFeatures", this.dbFeature);
    loadRelatedFeatures(this.dbFeature, this.getParent().getParent().layout);
    this._relatedFeaturesSet = true;
  }
  return this.dbFeature.relatedFeatures
}
